import {useContext} from "react";
import MessageContext from "../../context/MessageContext.tsx";
import {Button, Textarea, Tooltip} from "@mantine/core";
import { Copy as IconCopy } from "@phosphor-icons/react";
import {useEntityTypes} from "../../utils/api.ts";
import useCanUseClipboard from "../../hooks/useCanUseClipboard.ts";


interface CargoParserProps {
    onData: (data: Record<string, number>) => void;
    disabled: boolean;
}

export default function CargoParser({onData, disabled}: CargoParserProps) {
    const {success, error} = useContext(MessageContext);
    const {hasPermission, hasClipboard} = useCanUseClipboard();
    const {materials} = useEntityTypes(['materials']);

    if(disabled) {
        return (
            <>
                <Tooltip label="This is a premium feature">
                    <Button leftSection={<IconCopy size={16} />} disabled={true}>Load materials from clipboard</Button>
                </Tooltip>
            </>

        )
    }

    const process = (text: string) => {
        if (disabled) {
            return;
        }
        const update = simpleRMParser(text, materials);

        if(Object.keys(update).length > 0) {
            success('Data loaded', 'Data loaded successfully');
            onData(update);
        } else {
            error('Invalid data', 'No data found');
        }
    }

    if(hasClipboard) {
        return (
            <>
                {!hasPermission && <div><i className="text-red">Need to give clipboard access</i></div>}
                <Button leftSection={<IconCopy size={16} />} onClick={() => {
                    navigator.clipboard.readText().then(text => process(text)).catch(err => {
                        error('Load failed', err.message)
                    })
                }} disabled={disabled || !hasPermission}>Load materials from clipboard</Button>
            </>
        )
    } else {
        return (
            <>
                <Textarea value="" placeholder="Paste material list" onChange={e => process(e.target.value)} disabled={disabled} autosize={false} rows={1} style={{resize: 'none'}} />
            </>
        )
    }
}




function simpleRMParser (string: string, materials: MaterialType[]) {

    const result: Record<string, number> = {};
    if (!string) {
        return result;
    }

    const lines = string.trim().split('\n');

    const materialNameToIdMap = new Map<string, string>();
    for (const material of materials) {
        materialNameToIdMap.set(material.name.toLowerCase(), material.id);
    }

    // Heuristic to detect format 1 (e.g., "Quantum: 1,071")
    if (lines.some(line => line.includes(':'))) {
        for (const line of lines) {
            const parts = line.split(':');
            if (parts.length === 2) {
                const materialName = parts[0].trim().toLowerCase();
                const quantityStr = parts[1].trim().replace(/,/g, '');
                const quantity = parseInt(quantityStr, 10);
                const materialId = materialNameToIdMap.get(materialName);

                if (materialId && !isNaN(quantity)) {
                    result[materialId] = quantity;
                }
            }
        }
    } else { // Assume format 2 (table-like structure)
        for (const line of lines) {
            const parts = line.trim().split(/\s+/);
            if (parts.length > 1) {
                const materialName = parts[0].toLowerCase();
                const materialId = materialNameToIdMap.get(materialName);
                if (materialId) {
                    const quantityStr = parts[1].replace(/,/g, '');
                    const quantity = parseInt(quantityStr, 10);

                    if (!isNaN(quantity) && quantity > 0) {
                        result[materialId] = quantity;
                    }
                }
            }
        }
    }

    return result;



}
